<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue';

/**
 * The table on top and its selected record below, like classic Apple Mail. The
 * table keeps its full width. A divider between the two can be dragged up and
 * down, and the record pane's height is saved, so the next record opens at the
 * same height. The card split view saves its list's width the same way.
 *
 * With nothing selected there is no record pane, and the table fills the height.
 */
defineProps<{
    /** Whether a row is selected, so the record pane shows. */
    hasSelection: boolean;
}>();

const TABLE_RECORD_PANE_HEIGHT_KEY = 'table-record-pane-height';
/** Each part keeps at least this many pixels. */
const MIN_PART_HEIGHT = 120;
/** Before anything is saved, the record pane takes this share of the height. */
const DEFAULT_PANE_SHARE = 0.45;

const containerRef = ref<HTMLElement | null>(null);
const paneHeight = ref<number | null>(readStoredHeight());
const isResizing = ref(false);

function readStoredHeight(): number | null {
    try {
        const stored = Number(localStorage.getItem(TABLE_RECORD_PANE_HEIGHT_KEY));
        return Number.isFinite(stored) && stored >= MIN_PART_HEIGHT ? stored : null;
    } catch {
        return null;
    }
}

/** At least the pane's minimum, and never so tall that the table drops below its own. */
function clampToContainer(candidate: number): number {
    const containerHeight = containerRef.value?.clientHeight ?? 0;
    if (containerHeight <= 0) {
        return Math.max(MIN_PART_HEIGHT, candidate);
    }
    const ceiling = Math.max(MIN_PART_HEIGHT, containerHeight - MIN_PART_HEIGHT);
    return Math.max(MIN_PART_HEIGHT, Math.min(ceiling, candidate));
}

const paneStyle = computed(() =>
    paneHeight.value === null
        ? { height: `${DEFAULT_PANE_SHARE * 100}%`, minHeight: `${MIN_PART_HEIGHT}px` }
        : { height: `${clampToContainer(paneHeight.value)}px` },
);

let resizeStartY = 0;
let resizeStartHeight = 0;
const paneRef = ref<HTMLElement | null>(null);

function onResizeStart(event: PointerEvent) {
    event.preventDefault();
    isResizing.value = true;
    resizeStartY = event.clientY;
    resizeStartHeight = paneRef.value?.getBoundingClientRect().height ?? paneHeight.value ?? MIN_PART_HEIGHT;
    document.addEventListener('pointermove', onResizeMove);
    document.addEventListener('pointerup', onResizeEnd);
}

function onResizeMove(event: PointerEvent) {
    if (!isResizing.value) return;
    // Dragging the divider up makes the record pane taller.
    paneHeight.value = clampToContainer(resizeStartHeight + (resizeStartY - event.clientY));
}

function onResizeEnd() {
    if (!isResizing.value) return;
    isResizing.value = false;
    if (paneHeight.value !== null) {
        try {
            localStorage.setItem(TABLE_RECORD_PANE_HEIGHT_KEY, String(Math.round(paneHeight.value)));
        } catch {
            // Storage may be unavailable. The height still holds until the page reloads.
        }
    }
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
}

onBeforeUnmount(() => {
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
});

watchEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.cursor = isResizing.value ? 'row-resize' : '';
    document.body.style.userSelect = isResizing.value ? 'none' : '';
});

</script>

<template>
  <div ref="containerRef" data-testid="table-record-split-view" class="flex h-full min-h-0 w-full flex-col">
    <!-- The table. It scrolls on its own, so the sticky header sticks to it. -->
    <div class="min-h-0 flex-1 overflow-auto max-md:pb-mobile-area-bar-clearance" data-testid="table-record-split-table">
      <slot name="table" />
    </div>
    <template v-if="hasSelection">
      <!-- Pointer only. The keyboard moves between rows instead. -->
      <div
        data-testid="table-record-split-resizer"
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize record pane"
        class="h-1.5 shrink-0 cursor-row-resize bg-border/60 transition-colors hover:bg-primary/40 active:bg-primary/60"
        @pointerdown="onResizeStart"
      />
      <!-- `relative`, so labels hidden inside the record stay inside this scroll box. -->
      <div
        ref="paneRef"
        data-testid="table-record-pane"
        class="relative flex min-h-0 shrink-0 flex-col overflow-auto bg-background"
        :style="paneStyle"
      >
        <div class="flex min-h-full flex-col px-5 py-5">
          <slot name="record" />
        </div>
      </div>
    </template>
  </div>
</template>
